import React, { useState, useEffect, Suspense } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CategoryIcon from "@mui/icons-material/Category";
import ChatIcon from "@mui/icons-material/Chat";
import EmailIcon from "@mui/icons-material/Email";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CampaignIcon from "@mui/icons-material/Campaign";
import SchoolIcon from "@mui/icons-material/School";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WebIcon from "@mui/icons-material/Web";
import ListItemText from "@mui/material/ListItemText";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorefrontIcon from "@mui/icons-material/Storefront";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Home = React.lazy(() => import("../Admin/Dashboard"));
const Users = React.lazy(() => import("../Admin/Users"));
const ListingApproval = React.lazy(() => import("../Admin/ListingApproval"));
const Reports = React.lazy(() => import("../Admin/Reports"));
const CategoryManagement = React.lazy(() => import("../Admin/CategoryManagement"));
const Roadmap = React.lazy(() => import("../Admin/Roadmap"));
const Chat = React.lazy(() => import("../Admin/Chat"));
const Email = React.lazy(() => import("../Admin/Email"));
const Ecommerce = React.lazy(() => import("../Admin/Ecommerce"));
const Sponsors = React.lazy(() => import("../Admin/Sponsors"));
const Academy = React.lazy(() => import("../Admin/Academy"));
const Calendar = React.lazy(() => import("../Admin/Calendar"));
const Frontpages = React.lazy(() => import("../Admin/Frontpages"));
const Integrations = React.lazy(() => import("../Admin/Integrations"));

const drawerWidth = 240;
// const drawerWidthMobile = '100%'; // Commented out as it's currently unused

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 73,
  [theme.breakpoints.up("sm")]: {
    width: 73,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      width: drawerWidth,
      borderRight: "none",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      width: 83,
      borderRight: "none",
    },
  }),
}));

export default function Sidenav() {
  const theme = useTheme();
  const navigate = useNavigate();
  // Sidebar defaults to open on admin panel — user can collapse it via the
  // chevron and their choice sticks for the session (persisted in localStorage).
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("admin.sidenavOpen");
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("admin.sidenavOpen", String(open));
    } catch {}
  }, [open]);
  const [menuData, setMenuData] = useState("Home");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5000/api";
        const response = await axios.get(`${API_URL}/auth/me`, {
          withCredentials: true,
        });
        setIsAdmin(response.data.role === "admin");
      } catch (error) {
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // const visibleMenuItems = ['Home', 'Users', 'ListingApproval', 'Reports']; // Commented out as it's currently unused

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ backgroundColor: "#19474C", boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: "0.02em" }}>
            Exchanza Admin
          </Typography>
          <IconButton
            color="inherit"
            onClick={() => navigate("/")}
            sx={{
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "8px",
              px: 1.5,
              py: 0.5,
              gap: 0.5,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" } }}>
              Back to Site
            </Typography>
          </IconButton>
        </Toolbar>
      </AppBar>

      {open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: { xs: "block", md: "none" },
          }}
          onClick={handleDrawerClose}
        />
      )}

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "#19474C",
            color: "#fff",
            [theme.breakpoints.down("md")]: {
              width: open ? "240px" : 0,
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          },
        }}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
        <List>
          {isAdmin && (
            <>
              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Home")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dashboard"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Users")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Users"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("ListingApproval")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <AssignmentTurnedInIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Listing Approval"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Reports")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <AssignmentTurnedInIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Reports"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("CategoryManagement")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Categories"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Chat")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <ChatIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Chat"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Email")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Ecommerce")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <ShoppingCartIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ecommerce"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Sponsors")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <CampaignIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sponsors"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Integrations")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <IntegrationInstructionsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Integrations"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Academy")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <SchoolIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Academy"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Calendar")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <CalendarMonthIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Calendar"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem
                disablePadding
                sx={{ display: "block" }}
                onClick={() => setMenuData("Frontpages")}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : "auto",
                      justifyContent: "center",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    <WebIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Frontpages"
                    sx={{ opacity: open ? 1 : 0 }}
                  />
                </ListItemButton>
              </ListItem>
            </>
          )}

          <ListItem
            disablePadding
            sx={{ display: "block" }}
            onClick={() => setMenuData("Roadmap")}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <MapIcon />
              </ListItemIcon>
              <ListItemText
                primary="Roadmap"
                sx={{ opacity: open ? 1 : 0 }}
              />
            </ListItemButton>
          </ListItem>

        </List>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
        <Box sx={{ flexGrow: 1 }} />
        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
        <List>
          <ListItem
            disablePadding
            sx={{ display: "block" }}
            onClick={() => navigate("/")}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                <StorefrontIcon />
              </ListItemIcon>
              <ListItemText
                primary="Back to Site"
                sx={{ opacity: open ? 1 : 0 }}
                primaryTypographyProps={{ fontWeight: 500, color: "#fff" }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          // The permanent Drawer already occupies its width inside the parent
          // flex row, so flexGrow: 1 alone fills the rest. Do NOT also add
          // ml/width offsets — that stacks a second sidebar-width gap and
          // leaves an empty column between the drawer and the content.
          flexGrow: 1,
          minWidth: 0, // let inner tables/charts respect flex width instead
          p: 2,
          pl: 2,
          backgroundColor: "#F5F6F4",
          minHeight: "100vh",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <DrawerHeader />
        <Suspense fallback={<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress sx={{ color: "#19474C" }} /></Box>}>
          {menuData === "Home" && <Home />}
          {menuData === "Users" && <Users />}
          {menuData === "ListingApproval" && <ListingApproval />}
          {menuData === "Reports" && <Reports />}
          {menuData === "CategoryManagement" && <CategoryManagement />}
          {menuData === "Roadmap" && <Roadmap />}
          {menuData === "Chat" && <Chat />}
          {menuData === "Email" && <Email />}
          {menuData === "Ecommerce" && <Ecommerce />}
          {menuData === "Sponsors" && <Sponsors />}
          {menuData === "Integrations" && <Integrations />}
          {menuData === "Academy" && <Academy />}
          {menuData === "Calendar" && <Calendar />}
          {menuData === "Frontpages" && <Frontpages />}
        </Suspense>
      </Box>
    </Box>
  );
}
